import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Target, Award, Users, Globe, Heart, Zap } from 'lucide-react';
import SEO from '../components/SEO';
import LazyImage from '../components/LazyImage';

const About = () => {
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "About", url: "/about" }
  ];
  
  const values = [
    {
      icon: Award,
      title: 'Quality',
      description: 'We maintain a 99% first-batch acceptance rate through rigorous multi-layer QA processes and domain expertise.'
    },
    {
      icon: Users,
      title: 'Transparency',
      description: 'Open communication and clear processes ensure you always know the status and quality of your data operations.'
    },
    {
      icon: Zap,
      title: 'Scalability',
      description: 'From pilot projects to enterprise deployments, our operations scale seamlessly with your growing needs.'
    },
    {
      icon: Heart,
      title: 'Data Certainty',
      description: 'Our mission is to provide the confidence you need to deploy AI models that perform reliably in production.'
    }
  ];

  const team = [
    {
      name: 'Operations Leadership',
      role: '10+ years in BPO & Outsourcing',
      image: 'https://images.pexels.com/photos/3861964/pexels-photo-3861964.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      description: 'Expert team with deep experience in scaling data operations globally'
    },
    {
      name: 'AI Specialists',
      role: 'ML Engineers & Data Scientists',
      image: 'https://images.unsplash.com/photo-1622258415402-d6f597973b03?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHx0ZWNoJTIwdGVhbSUyMGNvbGxhYm9yYXRpb24lMjByZW1vdGUlMjB3b3JrJTIwQUklMjBkZXZlbG9wbWVudHxlbnwwfHx8fDE3NzQyOTg1OTZ8MA&ixlib=rb-4.1.0&q=85',
      description: 'Technical experts who understand production AI requirements'
    },
    {
      name: 'Domain Experts',
      role: 'Specialized Annotators',
      image: 'https://images.pexels.com/photos/1181275/pexels-photo-1181275.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      description: 'Trained specialists across medical, automotive, and other verticals'
    },
    {
      name: 'Quality Assurance',
      role: 'QA & Validation Teams',
      image: 'https://images.unsplash.com/photo-1646579886741-12b59840c63f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHw0fHx0ZWNoJTIwdGVhbSUyMGNvbGxhYm9yYXRpb24lMjByZW1vdGUlMjB3b3JrJTIwQUklMjBkZXZlbG9wbWVudHxlbnwwfHx8fDE3NzQyOTg1OTZ8MA&ixlib=rb-4.1.0&q=85',
      description: 'Dedicated teams ensuring every data point meets production standards'
    }
  ];

  const timeline = [
    { year: '2022', event: 'Founded with vision to solve AI data quality challenges' },
    { year: '2023', event: 'Launched specialized annotation services for autonomous vehicles and medical AI' },
    { year: '2024', event: 'Introduced human-in-the-loop validation and RLHF services' },
    { year: '2025', event: 'Reached 100+ projects milestone and launched end-to-end AI operations' },
    { year: '2026', event: 'Established Data Certainty framework and expanded globally' }
  ];

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      <SEO
        title="About Orboroi - AI Data Operations Experts"
        description="Meet the team behind production-ready AI data services. Quality-driven annotation, validation, and evaluation with 99% first-batch acceptance rate."
        canonical="/about"
        type="website"
        breadcrumbs={breadcrumbs}
      />
      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="text-[#379F7F]">Precision</span> in Every Pixel.
              <br />
              <span className="text-[#379F7F]">Certainty</span> in Your AI Models.
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Orboroi provides high-fidelity, human-verified data required for confident AI model 
              deployment across the entire lifecycle. We empower organizations to transform complex 
              datasets into production-ready applications.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-12 bg-black">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Target className="w-16 h-16 text-[#379F7F] mb-6" />
              <h2 className="text-4xl font-bold mb-6">Our Mission</h2>
              <p className="text-xl text-gray-300 leading-relaxed mb-6">
                We believe that the quality of training data directly determines the reliability 
                of AI systems in production. Our mission is to provide the highest-fidelity data 
                and evaluation services across every stage of the AI model lifecycle.
              </p>
              <p className="text-lg text-gray-400 leading-relaxed">
                With over 10 years of experience in BPO, outsourcing, and data operations, we've 
                built the expertise, processes, and teams required to deliver data certainty at scale.
              </p>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_lifecycle-preview/artifacts/2cukeee4_Our%20Mission.png"
                alt="Team collaboration"
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-lg"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-12 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Our <span className="text-[#379F7F]">Core Values</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              The principles that guide everything we do, from individual annotations to 
              enterprise-scale deployments.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card 
                key={index}
                className="bg-gray-800 border-gray-700 hover:border-[#379F7F] transition-all duration-300 hover:scale-105"
              >
                <CardHeader>
                  <value.icon className="w-12 h-12 text-[#379F7F] mb-4" />
                  <CardTitle className="text-white">{value.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-300">
                    {value.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-12 bg-gray-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Meet Our <span className="text-[#379F7F]">Expert Teams</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Diverse specialists with deep expertise across data operations, AI, and industry domains.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <Card 
                key={index}
                className="bg-gray-800 border-gray-700 hover:border-[#379F7F] transition-all duration-300 overflow-hidden group"
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-800 via-gray-800/50 to-transparent"></div>
                </div>
                <CardHeader className="-mt-20 relative z-10">
                  <CardTitle className="text-white">{member.name}</CardTitle>
                  <CardDescription className="text-[#379F7F] font-semibold">
                    {member.role}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300 text-sm">{member.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-12 bg-black">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Our <span className="text-[#379F7F]">Journey</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              From our founding to becoming a trusted partner for production AI across industries.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-gray-700 -translate-x-1/2 hidden lg:block"></div>

              {timeline.map((item, index) => (
                <div 
                  key={index}
                  className={`relative mb-12 flex flex-col lg:flex-row items-center ${
                    index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  }`}
                >
                  {/* Year Badge */}
                  <div className="lg:w-1/2 flex justify-center lg:justify-end lg:pr-8">
                    <div className={`${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left lg:pl-8'}`}>
                      <div className="inline-block bg-[#379F7F] text-black font-bold px-4 py-2 rounded-lg mb-2">
                        {item.year}
                      </div>
                    </div>
                  </div>

                  {/* Center dot */}
                  <div className="absolute left-1/2 w-4 h-4 bg-[#379F7F] rounded-full -translate-x-1/2 hidden lg:block"></div>

                  {/* Event */}
                  <div className="lg:w-1/2 flex justify-center lg:justify-start lg:pl-8">
                    <Card className={`bg-gray-800 border-gray-700 hover:border-[#379F7F] transition-all duration-300 max-w-sm ${
                      index % 2 !== 0 ? 'lg:ml-0' : 'lg:mr-0'
                    }`}>
                      <CardContent className="p-6">
                        <p className="text-gray-300">{item.event}</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold text-[#379F7F] mb-2">10+</div>
              <p className="text-gray-300">Years of Experience</p>
            </div>
            <div>
              <div className="text-5xl font-bold text-[#379F7F] mb-2">100+</div>
              <p className="text-gray-300">Projects Delivered</p>
            </div>
            <div>
              <div className="text-5xl font-bold text-[#379F7F] mb-2">99%</div>
              <p className="text-gray-300">Acceptance Rate</p>
            </div>
            <div>
              <div className="text-5xl font-bold text-[#379F7F] mb-2">500k+</div>
              <p className="text-gray-300">Annotated Assets</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Work with <span className="text-[#379F7F]">Orboroi</span>?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Let's discuss how our expertise and proven processes can help you achieve 
              data certainty and deploy production-ready AI models.
            </p>
            <Button 
              asChild
              size="lg"
              className="bg-[#379F7F] text-black hover:bg-[#379F7F]/90 font-semibold text-lg px-8 py-6"
            >
              <Link to="/contact">Get in Touch</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
